import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { termsConditionsService, TermsAcceptanceData, DocumentVersion } from '../services/termsConditionsService';
import EnhancedSignaturePad from './EnhancedSignaturePad';
import type { UserAccessLevel } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { getLegalDocumentBrand } from '../utils/branding';

export interface TermsConditionsModalProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  redRockId: string;
  onTermsAccepted: () => void;
  allowClose?: boolean; // Allow closing for all_access users
  userAccessLevel?: UserAccessLevel; // User access level
}

const steps = [
  'Terms & Conditions',
  'Consent to Use',
  'Consent to Disclosure'
];

const TermsConditionsModal: React.FC<TermsConditionsModalProps> = ({
  open,
  onClose,
  clientId,
  redRockId,
  onTermsAccepted,
  allowClose = false,
  userAccessLevel = 'normal',
}) => {
  const { brand, currentAssignment } = useAuth();
  /** TAL portal OR TAL Acquisition customer on Red Rock portal → TAL legal docs/UI */
  const docsBrand = getLegalDocumentBrand(brand, currentAssignment);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  
  // Document states
  const [tcDocument, setTcDocument] = useState<DocumentVersion | null>(null);
  const [consentUseDocument, setConsentUseDocument] = useState<DocumentVersion | null>(null);
  const [consentDisclosureDocument, setConsentDisclosureDocument] = useState<DocumentVersion | null>(null);
  
  // Step 1 states (T&C - Required)
  const [tcAccepted, setTcAccepted] = useState(false);
  const [tcSignatureData, setTcSignatureData] = useState<string>('');
  const [tcSignatureEmpty, setTcSignatureEmpty] = useState(true);
  const [tcSignatureType, setTcSignatureType] = useState<'draw' | 'type'>('draw');
  const [tcTypedName, setTcTypedName] = useState<string>('');
  const tcScrollRef = useRef<HTMLDivElement>(null);
  
  // Step 2 states (Consent to Use - Optional)
  const [consentUseSignatureData, setConsentUseSignatureData] = useState<string>('');
  const [consentUseSignatureEmpty, setConsentUseSignatureEmpty] = useState(true);
  const [consentUseSignatureType, setConsentUseSignatureType] = useState<'draw' | 'type'>('draw');
  const [consentUseTypedName, setConsentUseTypedName] = useState<string>('');
  const [consentUseTimePeriod, setConsentUseTimePeriod] = useState<number>(5); // 5 years default (forced)
  // TAL-only Consent to Use authorizations (optional; none selected by default)
  const [talConsentTaxPlanning, setTalConsentTaxPlanning] = useState(false);
  const [talConsentBookkeeping, setTalConsentBookkeeping] = useState(false);
  const consentUseScrollRef = useRef<HTMLDivElement>(null);
  
  // Step 3 states (Consent to Disclosure - Optional)
  const [consentDisclosureSignatureData, setConsentDisclosureSignatureData] = useState<string>('');
  const [consentDisclosureSignatureEmpty, setConsentDisclosureSignatureEmpty] = useState(true);
  const [consentDisclosureSignatureType, setConsentDisclosureSignatureType] = useState<'draw' | 'type'>('draw');
  const [consentDisclosureTypedName, setConsentDisclosureTypedName] = useState<string>('');
  const [consentDisclosureTimePeriod, setConsentDisclosureTimePeriod] = useState<number>(5); // 5 years default (forced)
  // TAL-only Consent to Disclosure authorization
  const [talDisclosureAuthorized, setTalDisclosureAuthorized] = useState(false);
  const consentDisclosureScrollRef = useRef<HTMLDivElement>(null);
  

  const resetAllStates = () => {
    setActiveStep(0);
    setTcAccepted(false);
    setTcSignatureData('');
    setTcSignatureEmpty(true);
    setTcSignatureType('draw');
    setTcTypedName('');
    setConsentUseSignatureData('');
    setConsentUseSignatureEmpty(true);
    setConsentUseSignatureType('draw');
    setConsentUseTypedName('');
    setConsentUseTimePeriod(5);
    setTalConsentTaxPlanning(false);
    setTalConsentBookkeeping(false);
    setConsentDisclosureSignatureData('');
    setConsentDisclosureSignatureEmpty(true);
    setConsentDisclosureSignatureType('draw');
    setConsentDisclosureTypedName('');
    setConsentDisclosureTimePeriod(5);
    setTalDisclosureAuthorized(false);
    setError(null);
  };

  // Fetch document content when modal opens (TAL host → TAL docs on any device)
  useEffect(() => {
    if (!open) return;

    resetAllStates();

    let cancelled = false;
    const loadDocuments = async () => {
      setDocumentsLoading(true);
      try {
        const [tcDoc, consentUseDoc, consentDisclosureDoc] = await Promise.all([
          termsConditionsService.getDocumentContent('T&C', docsBrand),
          termsConditionsService.getDocumentContent('CONSENT TO USE OF TAX RETURN INFORMATION', docsBrand),
          termsConditionsService.getDocumentContent('CONSENT TO DISCLOSURE OF TAX RETURN INFORMATION', docsBrand)
        ]);

        if (cancelled) return;
        setTcDocument(tcDoc);
        setConsentUseDocument(consentUseDoc);
        setConsentDisclosureDocument(consentDisclosureDoc);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch documents:', err);
        setError('Failed to load document content. Please try again.');
      } finally {
        if (!cancelled) setDocumentsLoading(false);
      }
    };

    void loadDocuments();
    return () => {
      cancelled = true;
    };
    // resetAllStates only clears local form state; intentionally omitted from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, docsBrand]);

  // Scroll to top when step changes
  useEffect(() => {
    if (open) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        // Scroll main modal content to top
        const dialogContent = document.querySelector('.MuiDialogContent-root');
        if (dialogContent && dialogContent instanceof HTMLElement) {
          dialogContent.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
        
        // Scroll inner document containers to top based on active step
        if (activeStep === 1 && consentUseScrollRef.current) {
          consentUseScrollRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        } else if (activeStep === 2 && consentDisclosureScrollRef.current) {
          consentDisclosureScrollRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        } else if (activeStep === 0 && tcScrollRef.current) {
          tcScrollRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [activeStep, open]);

  /**
   * Convert base64 data URL to File object
   */
  const dataURLtoFile = (dataurl: string, filename: string): File => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const scrollToBottom = (ref: React.RefObject<HTMLDivElement | null>) => {
    // Scroll inner scroller smoothly
    if (ref.current) {
      ref.current.scrollTo({
        top: ref.current.scrollHeight,
        behavior: 'smooth'
      });
    }
    // Scroll main modal to bottom smoothly - try multiple approaches to find scrollable container
    setTimeout(() => {
      // Try to find the DialogContent element
      const dialogContent = document.querySelector('.MuiDialogContent-root');
      if (dialogContent && dialogContent instanceof HTMLElement) {
        dialogContent.scrollTo({
          top: dialogContent.scrollHeight,
          behavior: 'smooth'
        });
      }
      
      // Also try to find any scrollable parent containers
      const dialog = document.querySelector('[role="dialog"]');
      if (dialog) {
        // Find all scrollable elements within the dialog
        const allElements = dialog.querySelectorAll('*');
        allElements.forEach((el) => {
          if (el instanceof HTMLElement) {
            const hasScroll = el.scrollHeight > el.clientHeight;
            if (hasScroll && (el.classList.contains('MuiDialogContent-root') || 
                el.style.overflow === 'auto' || 
                el.style.overflowY === 'auto' ||
                window.getComputedStyle(el).overflowY === 'auto' ||
                window.getComputedStyle(el).overflow === 'auto')) {
              el.scrollTo({
                top: el.scrollHeight,
                behavior: 'smooth'
              });
            }
          }
        });
      }
    }, 100);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Step 1: Must accept T&C and have signature
      if (!tcAccepted) {
        setError('Please accept the Terms & Conditions to continue.');
        return;
      }
      if (tcSignatureEmpty || !tcSignatureData) {
        setError('Please provide your signature to continue.');
        return;
      }
    } else if (activeStep === 1) {
      // Step 2: Consent to Use - require signature
      if (consentUseSignatureEmpty || !consentUseSignatureData) {
        setError('Please provide your signature to continue.');
        return;
      }
    } else if (activeStep === 2) {
      // Step 3: Consent to Disclosure - require signature
      if (consentDisclosureSignatureEmpty || !consentDisclosureSignatureData) {
        setError('Please provide your signature to continue.');
        return;
      }
    }
    setError(null);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  const handleSkipToNext = () => {
    setError(null);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFinish = async () => {
    setLoading(true);
    setError(null);

    try {
      const ipAddress = await termsConditionsService.getClientIP();
      const currentDate = new Date().toISOString();
      const signedDate = new Date().toISOString().split('T')[0];
      const deviceType = termsConditionsService.detectDeviceType();

      const promises = [];

      const isTalAcquisite = docsBrand === 'tal';

      // Step 1: T&C (Required - must have signature)
      if (tcDocument && tcAccepted && tcSignatureData) {
        const tcData: TermsAcceptanceData = {
          red_rock_id: redRockId,
          client_id: clientId,
          document_version: tcDocument.id,
          signed_data: currentDate,
          signed_date: signedDate,
          ip_address: ipAddress,
          manual_signature_option: true,
          signature_device_type: deviceType,
          is_tal_acquisite: isTalAcquisite,
        };
        
        // If typed name is used, save name field; otherwise save signature file
        if (tcSignatureType === 'type' && tcTypedName.trim()) {
          tcData.name = tcTypedName.trim();
        } else {
          const tcSignatureFile = dataURLtoFile(tcSignatureData, `signature_tc_${Date.now()}.png`);
          tcData.signature = tcSignatureFile;
        }
        
        promises.push(termsConditionsService.acceptTerms(tcData));
      }

      // Step 2: Consent to Use (Optional - only if signed)
      if (consentUseDocument && consentUseSignatureData && consentUseSignatureData.trim() !== '' && !consentUseSignatureEmpty) {
        const consentUseData: TermsAcceptanceData = {
          red_rock_id: redRockId,
          client_id: clientId,
          document_version: consentUseDocument.id,
          signed_data: currentDate,
          signed_date: signedDate,
          ip_address: ipAddress,
          manual_signature_option: true,
          signature_device_type: deviceType,
          is_tal_acquisite: isTalAcquisite,
          checkbox_data: {
            consent_duration: consentUseTimePeriod,
          },
        };
        
        // If typed name is used, save name field; otherwise save signature file
        if (consentUseSignatureType === 'type' && consentUseTypedName.trim()) {
          consentUseData.name = consentUseTypedName.trim();
        } else {
          const consentUseSignatureFile = dataURLtoFile(consentUseSignatureData, `signature_consent_use_${Date.now()}.png`);
          consentUseData.signature = consentUseSignatureFile;
        }
        
        promises.push(termsConditionsService.acceptTerms(consentUseData));
      }

      // Step 3: Consent to Disclosure (Optional - only if signed)
      if (consentDisclosureDocument && consentDisclosureSignatureData && consentDisclosureSignatureData.trim() !== '' && !consentDisclosureSignatureEmpty) {
        const consentDisclosureData: TermsAcceptanceData = {
          red_rock_id: redRockId,
          client_id: clientId,
          document_version: consentDisclosureDocument.id,
          signed_data: currentDate,
          signed_date: signedDate,
          ip_address: ipAddress,
          manual_signature_option: true,
          signature_device_type: deviceType,
          is_tal_acquisite: isTalAcquisite,
          checkbox_data: {
            consent_duration: consentDisclosureTimePeriod,
          },
        };
        
        // If typed name is used, save name field; otherwise save signature file
        if (consentDisclosureSignatureType === 'type' && consentDisclosureTypedName.trim()) {
          consentDisclosureData.name = consentDisclosureTypedName.trim();
        } else {
          const consentDisclosureSignatureFile = dataURLtoFile(consentDisclosureSignatureData, `signature_consent_disclosure_${Date.now()}.png`);
          consentDisclosureData.signature = consentDisclosureSignatureFile;
        }
        
        promises.push(termsConditionsService.acceptTerms(consentDisclosureData));
      }

      // At minimum, T&C must be accepted
      if (promises.length === 0) {
        setError('Please complete at least the Terms & Conditions step.');
        setLoading(false);
        return;
      }

      await Promise.all(promises);

      onTermsAccepted();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to accept terms and conditions');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && allowClose) {
      onClose();
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LockIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary">
                  Terms & Conditions
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<KeyboardArrowDownIcon />}
                onClick={() => scrollToBottom(tcScrollRef)}
                sx={{ ml: 2 }}
              >
                Skip to Signature
              </Button>
            </Box>
            
            <Box
              ref={tcScrollRef}
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              {documentsLoading ? (
                <Typography variant="body2" color="textSecondary">
                  Loading document content...
                </Typography>
              ) : tcDocument ? (
                <Box
                  sx={{
                    fontSize: '0.875rem',
                    '& p': { marginBottom: '15px' },
                    '& strong': { fontWeight: 'bold' }
                  }}
                  dangerouslySetInnerHTML={{ __html: tcDocument.document_data }}
                />
              ) : (
                <Typography variant="body2" color="error">
                  Failed to load Terms & Conditions content
                </Typography>
              )}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={tcAccepted}
                  onChange={(e) => setTcAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  I have read and agree to the Terms & Conditions
                </Typography>
              }
            />

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" color="primary" gutterBottom sx={{ mb: 2 }}>
                Signature Required
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please sign below to acknowledge that you have read and agree to the terms and conditions.
              </Typography>
              
              <EnhancedSignaturePad
                key="tc-signature"
                initialData={tcSignatureData}
                signatureType={tcSignatureType}
                typedName={tcTypedName}
                drawSignatureDisabled={termsConditionsService.isIpad()}
                onSignatureChange={(isEmpty) => {
                  setTcSignatureEmpty(isEmpty);
                }}
                onSignatureData={(dataUrl) => {
                  setTcSignatureData(dataUrl);
                }}
                onSignatureTypeChange={(type) => {
                  setTcSignatureType(type);
                }}
                onTypedNameChange={(name) => {
                  setTcTypedName(name);
                }}
                disabled={loading}
              />
            </Box>
          </Paper>
        );

      case 1:
        return (
          <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LockIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary">
                  Consent to Use of Tax Return Information
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<KeyboardArrowDownIcon />}
                onClick={() => scrollToBottom(consentUseScrollRef)}
                sx={{ ml: 2 }}
              >
                Skip to Signature
              </Button>
            </Box>
            
            <Box
              ref={consentUseScrollRef}
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              {documentsLoading ? (
                <Typography variant="body2" color="textSecondary">
                  Loading document content...
                </Typography>
              ) : consentUseDocument ? (
                <Box
                  sx={{
                    fontSize: '0.875rem',
                    '& p': { marginBottom: '15px' },
                    '& strong': { fontWeight: 'bold' }
                  }}
                  dangerouslySetInnerHTML={{ __html: consentUseDocument.document_data }}
                />
              ) : (
                <Typography variant="body2" color="error">
                  Failed to load Consent to Use content
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              {docsBrand === 'tal' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    This is optional. Check only the specific uses you authorize. None is selected by
                    default. Do not use this form to authorize standard preparation uses that law
                    permits without consent.
                  </Typography>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', mb: 1.5, mr: 0 }}
                    control={
                      <Checkbox
                        checked={talConsentTaxPlanning}
                        onChange={(e) => setTalConsentTaxPlanning(e.target.checked)}
                        disabled={loading}
                        sx={{ pt: 0.25 }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I authorize The Accounting Lab, LLC to use information furnished for my tax
                        preparation to identify and contact me about its own separately offered
                        tax-planning and tax-advisory services. This consent does not permit
                        disclosure to another company.
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', mb: 1, mr: 0 }}
                    control={
                      <Checkbox
                        checked={talConsentBookkeeping}
                        onChange={(e) => setTalConsentBookkeeping(e.target.checked)}
                        disabled={loading}
                        sx={{ pt: 0.25 }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I authorize The Accounting Lab, LLC to use information furnished for my tax
                        preparation to identify and contact me about its own separately offered
                        bookkeeping and accounting services. This consent does not permit disclosure
                        to another company.
                      </Typography>
                    }
                  />
                </Box>
              )}

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                  Consent Duration
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  Select the duration for which this consent will be valid
                </Typography>
                <ToggleButtonGroup
                  value={consentUseTimePeriod}
                  exclusive
                  onChange={(_, value) => {
                    if (value !== null) {
                      setConsentUseTimePeriod(value);
                    }
                  }}
                  aria-label="consent duration"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    '& .MuiToggleButton-root': {
                      flex: '1 1 auto',
                      minWidth: '80px',
                      px: 2.5,
                      py: 1.5,
                      fontWeight: 500,
                      fontSize: '0.9375rem',
                      textTransform: 'none',
                      border: '1.5px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        borderColor: 'primary.main',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          borderColor: 'primary.dark',
                        },
                      },
                    },
                  }}
                >
                  {[5, 4, 3, 2, 1].map((year) => (
                    <ToggleButton key={year} value={year} aria-label={`${year} ${year === 1 ? 'year' : 'years'}`}>
                      {year} {year === 1 ? 'Year' : 'Years'}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                  Signature
                </Typography>
                
                <EnhancedSignaturePad
                  key="consent-use-signature"
                  initialData={consentUseSignatureData}
                  signatureType={consentUseSignatureType}
                  typedName={consentUseTypedName}
                  drawSignatureDisabled={termsConditionsService.isIpad()}
                  onSignatureChange={(isEmpty) => {
                    setConsentUseSignatureEmpty(isEmpty);
                  }}
                  onSignatureData={(dataUrl) => {
                    setConsentUseSignatureData(dataUrl);
                  }}
                  onSignatureTypeChange={(type) => {
                    setConsentUseSignatureType(type);
                  }}
                  onTypedNameChange={(name) => {
                    setConsentUseTypedName(name);
                  }}
                  disabled={loading}
                />
              </Box>
            </Box>
          </Paper>
        );

      case 2:
        return (
          <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LockIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary">
                  Consent to Disclosure of Tax Return Information
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<KeyboardArrowDownIcon />}
                onClick={() => scrollToBottom(consentDisclosureScrollRef)}
                sx={{ ml: 2 }}
              >
                Skip to Signature
              </Button>
            </Box>
            
            <Box
              ref={consentDisclosureScrollRef}
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              {documentsLoading ? (
                <Typography variant="body2" color="textSecondary">
                  Loading document content...
                </Typography>
              ) : consentDisclosureDocument ? (
                <Box
                  sx={{
                    fontSize: '0.875rem',
                    '& p': { marginBottom: '15px' },
                    '& strong': { fontWeight: 'bold' }
                  }}
                  dangerouslySetInnerHTML={{ __html: consentDisclosureDocument.document_data }}
                />
              ) : (
                <Typography variant="body2" color="error">
                  Failed to load Consent to Disclosure content
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              {docsBrand === 'tal' && (
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', mb: 1, mr: 0 }}
                    control={
                      <Checkbox
                        checked={talDisclosureAuthorized}
                        onChange={(e) => setTalDisclosureAuthorized(e.target.checked)}
                        disabled={loading}
                        sx={{ pt: 0.25 }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I authorize The Accounting Lab, LLC to disclose only the tax return
                        information specified below to the specifically identified recipient below
                        for the specifically stated purpose below.
                      </Typography>
                    }
                  />
                </Box>
              )}

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                  Consent Duration
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  Select the duration for which this consent will be valid
                </Typography>
                <ToggleButtonGroup
                  value={consentDisclosureTimePeriod}
                  exclusive
                  onChange={(_, value) => {
                    if (value !== null) {
                      setConsentDisclosureTimePeriod(value);
                    }
                  }}
                  aria-label="consent duration"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    '& .MuiToggleButton-root': {
                      flex: '1 1 auto',
                      minWidth: '80px',
                      px: 2.5,
                      py: 1.5,
                      fontWeight: 500,
                      fontSize: '0.9375rem',
                      textTransform: 'none',
                      border: '1.5px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        borderColor: 'primary.main',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          borderColor: 'primary.dark',
                        },
                      },
                    },
                  }}
                >
                  {[5, 4, 3, 2, 1].map((year) => (
                    <ToggleButton key={year} value={year} aria-label={`${year} ${year === 1 ? 'year' : 'years'}`}>
                      {year} {year === 1 ? 'Year' : 'Years'}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                  Signature
                </Typography>
                
                <EnhancedSignaturePad
                  key="consent-disclosure-signature"
                  initialData={consentDisclosureSignatureData}
                  signatureType={consentDisclosureSignatureType}
                  typedName={consentDisclosureTypedName}
                  drawSignatureDisabled={termsConditionsService.isIpad()}
                  onSignatureChange={(isEmpty) => {
                    setConsentDisclosureSignatureEmpty(isEmpty);
                  }}
                  onSignatureData={(dataUrl) => {
                    setConsentDisclosureSignatureData(dataUrl);
                  }}
                  onSignatureTypeChange={(type) => {
                    setConsentDisclosureSignatureType(type);
                  }}
                  onTypedNameChange={(name) => {
                    setConsentDisclosureTypedName(name);
                  }}
                  disabled={loading}
                />
              </Box>
            </Box>
          </Paper>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={allowClose ? handleClose : undefined}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={!allowClose}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LockIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Legal Agreements Required</Typography>
        </Box>
        {allowClose && (
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {allowClose && (userAccessLevel === 'all_access' || userAccessLevel === 'admin') ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>View Mode:</strong> You can close this modal to view customer data.
              However, the customer must accept these terms and conditions to proceed with actions.
              Use the red indicator to reopen this modal when the customer is ready to accept.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Before accessing your account, you must complete the following legal agreements.
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0 || loading}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep === steps.length - 1 ? (
          <>
            <Button
              onClick={handleFinish}
              variant="outlined"
              size="large"
              disabled={loading}
              sx={{
                minWidth: 150,
                color: 'text.disabled',
                borderColor: 'action.disabled',
                '&:hover': {
                  borderColor: 'action.disabled',
                  bgcolor: 'action.hover',
                },
              }}
            >
              Skip
            </Button>
            <Button
              onClick={handleFinish}
              variant="contained"
              color="primary"
              size="large"
              disabled={loading || consentDisclosureSignatureEmpty || !consentDisclosureSignatureData}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              sx={{ minWidth: 200 }}
            >
              {loading ? 'Processing...' : 'Finish'}
            </Button>
          </>
        ) : (
          <>
            {activeStep === 1 && (
              <Button
                onClick={handleSkipToNext}
                variant="outlined"
                size="large"
                disabled={loading}
                sx={{
                  minWidth: 150,
                  color: 'text.disabled',
                  borderColor: 'action.disabled',
                  '&:hover': {
                    borderColor: 'action.disabled',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                Skip & Continue
              </Button>
            )}
            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              size="large"
              disabled={
                loading || 
                (activeStep === 0 && (!tcAccepted || tcSignatureEmpty || !tcSignatureData)) ||
                (activeStep === 1 && (consentUseSignatureEmpty || !consentUseSignatureData)) ||
                (activeStep === 2 && (consentDisclosureSignatureEmpty || !consentDisclosureSignatureData))
              }
              endIcon={<ArrowForwardIcon />}
              sx={{ minWidth: 200 }}
            >
              Next
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TermsConditionsModal;
